import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  uploadLectureSolvedImage,
  uploadImageMapReadingImage,
  deleteLectureSolvedImage,
  normalizeSolvedImages,
} from '../utils/lectureStorage';
import { blankImageMapMcq, importImageMapJson, normalizeImageMapQuestions, sampleImageMapJsonText } from '../utils/imageMapReadings';
import InquiriesPanel from '../components/admin/InquiriesPanel';
import UnpaidEnrollmentsPanel from '../components/admin/UnpaidEnrollmentsPanel';
import SubscriptionsPanel from '../components/admin/SubscriptionsPanel';
import PricingPlansPanel from '../components/admin/PricingPlansPanel';
import AdminPaymentsPanel from '../components/admin/AdminPaymentsPanel';
import AdminMessagesPanel from '../components/admin/AdminMessagesPanel';
import AdminStudentProgressPanel from '../components/admin/AdminStudentProgressPanel';
import AdminDashboardPanel from '../components/admin/AdminDashboardPanel';
import QuestionBankPanel from '../components/admin/QuestionBankPanel';
import AdminNotifications from '../components/admin/AdminNotifications';
import LectureEditor from '../components/admin/LectureEditor';
import QuizMcqEditor from '../components/admin/QuizMcqEditor';
import {
  blocksFromDay,
  blocksHaveStudentContent,
  emptyLectureSection,
  hasAuthoredLecture,
  persistableLectureBlocks,
  serializeLectureBlocks,
} from '../utils/lectureContent';
import { editorQuestionsFrom, persistableQuizQuestions } from '../utils/authoredQuiz';
import {
  newYoutubeVideo,
  normalizeYoutubeVideos,
  persistableYoutubeVideos,
} from '../utils/youtube';
import AdminShell from '../components/layout/AdminShell';
import { getAdminNavItems, getAdminPageMeta } from '../utils/adminNavItems';
import { SUBJECTS_THEME } from '../theme';

const SUBJECTS = SUBJECTS_THEME;
const SUBJECT_IDS = new Set(SUBJECTS.map((s) => s.id));
const ADMIN_PANEL_TABS = new Set([
  'dashboard',
  'inquiries',
  'unpaid-enrollments',
  'subscriptions',
  'pricing',
  'payments',
  'exam-docs',
  'messages',
  'student-progress',
]);

export default function AdminPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get('tab') || '';
  const [activeSubject, setActiveSubject] = useState(() => {
    if (ADMIN_PANEL_TABS.has(tabFromUrl)) return tabFromUrl;
    if (SUBJECT_IDS.has(tabFromUrl)) return tabFromUrl;
    return 'dashboard';
  });

  useEffect(() => {
    const t = searchParams.get('tab') || '';
    if (ADMIN_PANEL_TABS.has(t) || SUBJECT_IDS.has(t)) {
      setActiveSubject(t);
    } else if (!t) {
      setActiveSubject('dashboard');
    }
  }, [searchParams]);

  function selectTab(id) {
    setActiveSubject(id);
    setSearchParams({ tab: id });
  }

  const navActiveKey = useMemo(() => {
    if (ADMIN_PANEL_TABS.has(activeSubject) || SUBJECT_IDS.has(activeSubject)) {
      return activeSubject;
    }
    return 'dashboard';
  }, [activeSubject]);

  const adminNav = useMemo(() => getAdminNavItems(navActiveKey), [navActiveKey]);
  const pageMeta = useMemo(() => getAdminPageMeta(navActiveKey), [navActiveKey]);
  const isCourseTab = SUBJECT_IDS.has(activeSubject);

  const [modules, setModules] = useState([]);
  const [daysByModule, setDaysByModule] = useState({});
  const [selectedModuleId, setSelectedModuleId] = useState(null);

  const [showModuleModal, setShowModuleModal] = useState(false);
  const [moduleForm, setModuleForm] = useState({ title: '', quizQuestions: editorQuestionsFrom([]) });
  const [editingModuleId, setEditingModuleId] = useState(null);

  const [showDayModal, setShowDayModal] = useState(false);
  const [dayForm, setDayForm] = useState({
    title: '',
    topic: '',
    solvedImages: [],
    isRevisionLecture: false,
    contentBlocks: [emptyLectureSection()],
    source: 'editor',
    youtubeVideos: [],
    quizQuestions: editorQuestionsFrom([]),
  });
  const [dayImageUploading, setDayImageUploading] = useState(false);
  const [editingDayId, setEditingDayId] = useState(null);
  const [replacingImageId, setReplacingImageId] = useState(null);

  const [extendedResponses, setExtendedResponses] = useState([]);
  const [showErModal, setShowErModal] = useState(false);
  const [editingErId, setEditingErId] = useState(null);
  const [erForm, setErForm] = useState({ title: '', prompt: '', modelAnswer: '' });

  const [imageMapReadings, setImageMapReadings] = useState([]);
  const [showImrModal, setShowImrModal] = useState(false);
  const [editingImrId, setEditingImrId] = useState(null);
  const [imrForm, setImrForm] = useState({
    title: '',
    readingText: '',
    images: [],
    questions: [blankImageMapMcq()],
  });
  const [imrImageUploading, setImrImageUploading] = useState(false);
  const [imrJsonError, setImrJsonError] = useState('');
  const [replacingImrImageId, setReplacingImrImageId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!isAdmin) navigate('/dashboard');
  }, [isAdmin, navigate]);

  useEffect(() => {
    const dayUnsubs = {};
    const modulesUnsub = onSnapshot(
      query(collection(db, 'modules'), orderBy('order', 'asc')),
      (snap) => {
        const mods = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setModules(mods);
        mods.forEach((mod) => {
          if (!dayUnsubs[mod.id]) {
            dayUnsubs[mod.id] = onSnapshot(
              query(collection(db, 'modules', mod.id, 'days'), orderBy('order', 'asc')),
              (dSnap) => {
                const days = dSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setDaysByModule((prev) => ({ ...prev, [mod.id]: days }));
              }
            );
          }
        });
      }
    );
    return () => {
      modulesUnsub();
      Object.values(dayUnsubs).forEach((u) => u());
    };
  }, []);

  useEffect(() => {
    if (activeSubject !== 'language-arts') return undefined;
    const unsub = onSnapshot(
      collection(db, 'extendedResponses'),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((row) => !row.subject || row.subject === 'language-arts')
          .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setExtendedResponses(rows);
      },
      () => setExtendedResponses([])
    );
    return () => unsub();
  }, [activeSubject]);

  useEffect(() => {
    if (activeSubject !== 'social-studies') return undefined;
    const unsub = onSnapshot(
      collection(db, 'imageMapReadings'),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((row) => !row.subject || row.subject === 'social-studies')
          .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setImageMapReadings(rows);
      },
      () => setImageMapReadings([])
    );
    return () => unsub();
  }, [activeSubject]);

  // Reset module selection when subject tab changes
  useEffect(() => {
    setSelectedModuleId(null);
  }, [activeSubject]);

  const filteredModules = modules.filter((m) => m.subject === activeSubject);
  const selectedModule = modules.find((m) => m.id === selectedModuleId);
  const selectedDays = selectedModuleId ? daysByModule[selectedModuleId] || [] : [];
  const activeSubjectData = SUBJECTS.find((s) => s.id === activeSubject);

  /* ── Module CRUD ── */
  function openAddModule() {
    setEditingModuleId(null);
    setModuleForm({ title: '', quizQuestions: editorQuestionsFrom([]) });
    setShowModuleModal(true);
  }

  function openEditModule(mod) {
    setEditingModuleId(mod.id);
    setModuleForm({
      title: mod.title,
      quizQuestions: editorQuestionsFrom(mod.quizQuestions),
    });
    setShowModuleModal(true);
  }

  async function saveModule() {
    if (!moduleForm.title.trim()) return;
    setSaving(true);
    try {
      if (editingModuleId) {
        await updateDoc(doc(db, 'modules', editingModuleId), {
          title: moduleForm.title.trim(),
          quizQuestions: persistableQuizQuestions(moduleForm.quizQuestions),
        });
      } else {
        const subjectMods = modules.filter((m) => m.subject === activeSubject);
        const ref = await addDoc(collection(db, 'modules'), {
          title: moduleForm.title.trim(),
          subject: activeSubject,
          order: subjectMods.length + 1,
          createdAt: serverTimestamp(),
          quizQuestions: persistableQuizQuestions(moduleForm.quizQuestions),
        });
        setSelectedModuleId(ref.id);
      }
      setShowModuleModal(false);
    } finally {
      setSaving(false);
    }
  }

  async function executeDelete() {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      if (deleteConfirm.type === 'extended') {
        await deleteDoc(doc(db, 'extendedResponses', deleteConfirm.id));
      } else if (deleteConfirm.type === 'image-map') {
        const item = imageMapReadings.find((row) => row.id === deleteConfirm.id);
        for (const img of item?.images || []) {
          if (img?.path) await deleteLectureSolvedImage(img.path);
        }
        await deleteDoc(doc(db, 'imageMapReadings', deleteConfirm.id));
      } else if (deleteConfirm.type === 'module') {
        const daysSnap = await getDocs(
          collection(db, 'modules', deleteConfirm.id, 'days')
        );
        const batch = writeBatch(db);
        daysSnap.forEach((d) => batch.delete(d.ref));
        batch.delete(doc(db, 'modules', deleteConfirm.id));
        await batch.commit();
        if (selectedModuleId === deleteConfirm.id) setSelectedModuleId(null);
      } else {
        await deleteDoc(
          doc(db, 'modules', selectedModuleId, 'days', deleteConfirm.id)
        );
      }
    } finally {
      setSaving(false);
      setDeleteConfirm(null);
    }
  }

  function openAddEr() {
    setEditingErId(null);
    setErForm({ title: '', prompt: '', modelAnswer: '' });
    setShowErModal(true);
  }

  function openEditEr(item) {
    setEditingErId(item.id);
    setErForm({
      title: item.title || '',
      prompt: item.prompt || '',
      modelAnswer: item.modelAnswer || '',
    });
    setShowErModal(true);
  }

  async function saveExtendedResponse() {
    if (!erForm.title.trim() || !erForm.prompt.trim() || !erForm.modelAnswer.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: erForm.title.trim(),
        prompt: erForm.prompt.trim(),
        modelAnswer: erForm.modelAnswer.trim(),
        subject: 'language-arts',
      };
      if (editingErId) {
        await updateDoc(doc(db, 'extendedResponses', editingErId), payload);
      } else {
        await addDoc(collection(db, 'extendedResponses'), {
          ...payload,
          order: extendedResponses.length + 1,
          createdAt: serverTimestamp(),
        });
      }
      setShowErModal(false);
    } finally {
      setSaving(false);
    }
  }

  function openAddImr() {
    setEditingImrId(null);
    setImrForm({
      title: '',
      readingText: '',
      images: [],
      questions: [blankImageMapMcq()],
    });
    setShowImrModal(true);
  }

  function openEditImr(item) {
    setEditingImrId(item.id);
    const questions = normalizeImageMapQuestions(item.questions);
    setImrForm({
      title: item.title || '',
      readingText: item.readingText || '',
      images: Array.isArray(item.images) ? item.images.filter((img) => img?.url) : [],
      questions: questions.length ? questions : [blankImageMapMcq()],
    });
    setShowImrModal(true);
  }

  async function handleImrImageUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImrImageUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const { url, path } = await uploadImageMapReadingImage(file, editingImrId || 'draft');
        uploaded.push({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          url,
          path,
          description: '',
        });
      }
      setImrForm((p) => ({ ...p, images: [...(p.images || []), ...uploaded] }));
    } catch (err) {
      alert(err.message || 'Image upload failed');
    } finally {
      setImrImageUploading(false);
      e.target.value = '';
    }
  }

  async function handleReplaceImrImage(e, imageId) {
    const file = e.target.files?.[0];
    if (!file || !imageId) return;
    setReplacingImrImageId(imageId);
    try {
      const existing = (imrForm.images || []).find((img) => img.id === imageId);
      const { url, path } = await uploadImageMapReadingImage(file, editingImrId || 'draft');
      if (existing?.path) await deleteLectureSolvedImage(existing.path);
      setImrForm((p) => ({
        ...p,
        images: (p.images || []).map((img) => (img.id === imageId ? { ...img, url, path } : img)),
      }));
    } catch (err) {
      alert(err.message || 'Image replace failed');
    } finally {
      setReplacingImrImageId(null);
      e.target.value = '';
    }
  }

  async function handleDeleteImrImage(imageId) {
    const existing = (imrForm.images || []).find((img) => img.id === imageId);
    if (!existing) return;
    if (!window.confirm('Remove this visual image? It will be deleted from storage.')) return;
    if (existing.path) await deleteLectureSolvedImage(existing.path);
    setImrForm((p) => ({
      ...p,
      images: (p.images || []).filter((img) => img.id !== imageId),
    }));
  }

  function updateImrQuestion(index, patch) {
    setImrForm((p) => ({
      ...p,
      questions: (p.questions || []).map((q, i) => (i === index ? { ...q, ...patch } : q)),
    }));
  }

  function updateImrOption(qIndex, optIndex, value) {
    setImrForm((p) => ({
      ...p,
      questions: (p.questions || []).map((q, i) => {
        if (i !== qIndex) return q;
        const options = [...(q.options || ['', '', '', ''])];
        options[optIndex] = value;
        return { ...q, options };
      }),
    }));
  }

  const imrValidQuestions = normalizeImageMapQuestions(imrForm.questions);
  const canSaveImr =
    Boolean(imrForm.title.trim()) &&
    (imrForm.images || []).some((img) => img?.url) &&
    imrValidQuestions.length > 0;

  async function saveImageMapReading() {
    if (!canSaveImr) return;
    setSaving(true);
    try {
      const payload = {
        title: imrForm.title.trim(),
        readingText: (imrForm.readingText || '').trim(),
        subject: 'social-studies',
        images: (imrForm.images || [])
          .filter((img) => img?.url)
          .map((img) => ({
            id: img.id,
            url: img.url,
            path: img.path || '',
            description: (img.description || '').trim(),
          })),
        questions: imrValidQuestions,
      };
      if (editingImrId) {
        await updateDoc(doc(db, 'imageMapReadings', editingImrId), payload);
      } else {
        await addDoc(collection(db, 'imageMapReadings'), {
          ...payload,
          order: imageMapReadings.length + 1,
          createdAt: serverTimestamp(),
        });
      }
      setShowImrModal(false);
    } finally {
      setSaving(false);
    }
  }

  /* ── Day CRUD ── */
  function openAddDay() {
    setEditingDayId(null);
    setDayForm({
      title: '',
      topic: '',
      solvedImages: [],
      isRevisionLecture: false,
      contentBlocks: [emptyLectureSection()],
      source: 'editor',
      youtubeVideos: [],
      quizQuestions: editorQuestionsFrom([]),
    });
    setShowDayModal(true);
  }

  function openAddRevisionDay() {
    setEditingDayId(null);
    setDayForm({
      title: 'Module Revision',
      topic: '',
      solvedImages: [],
      isRevisionLecture: true,
      contentBlocks: [emptyLectureSection('Revision Warm-Up')],
      source: 'editor',
      youtubeVideos: [],
      quizQuestions: editorQuestionsFrom([]),
    });
    setShowDayModal(true);
  }

  function openEditDay(day) {
    setEditingDayId(day.id);
    setDayForm({
      title: day.title,
      topic: day.topic || '',
      solvedImages: normalizeSolvedImages(day),
      isRevisionLecture: Boolean(day.isRevisionLecture),
      contentBlocks: blocksFromDay(day),
      source: day.source || (hasAuthoredLecture(day) ? 'editor' : 'ai-topic'),
      youtubeVideos: normalizeYoutubeVideos(day.youtubeVideos),
      quizQuestions: editorQuestionsFrom(day.quizQuestions),
    });
    setShowDayModal(true);
  }

  async function handleDayImageUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedModuleId) return;
    setDayImageUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const { url, path } = await uploadLectureSolvedImage(
          file,
          selectedModuleId,
          editingDayId || 'draft'
        );
        uploaded.push({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          url,
          path,
          description: '',
        });
      }
      setDayForm((p) => ({ ...p, solvedImages: [...(p.solvedImages || []), ...uploaded] }));
    } catch (err) {
      alert(err.message || 'Image upload failed');
    } finally {
      setDayImageUploading(false);
      e.target.value = '';
    }
  }

  async function handleReplaceDayImage(e, imageId) {
    const file = e.target.files?.[0];
    if (!file || !selectedModuleId || !imageId) return;
    setReplacingImageId(imageId);
    try {
      const existing = (dayForm.solvedImages || []).find((img) => img.id === imageId);
      const { url, path } = await uploadLectureSolvedImage(
        file,
        selectedModuleId,
        editingDayId || 'draft'
      );
      if (existing?.path) {
        await deleteLectureSolvedImage(existing.path);
      }
      setDayForm((p) => ({
        ...p,
        solvedImages: (p.solvedImages || []).map((img) =>
          img.id === imageId ? { ...img, url, path } : img
        ),
      }));
    } catch (err) {
      alert(err.message || 'Image replace failed');
    } finally {
      setReplacingImageId(null);
      e.target.value = '';
    }
  }

  async function handleDeleteDayImage(imageId) {
    const existing = (dayForm.solvedImages || []).find((img) => img.id === imageId);
    if (!existing) return;
    if (!window.confirm('Remove this image from the lecture? It will be deleted from storage.')) {
      return;
    }
    if (existing.path) {
      await deleteLectureSolvedImage(existing.path);
    }
    setDayForm((p) => ({
      ...p,
      solvedImages: (p.solvedImages || []).filter((img) => img.id !== imageId),
    }));
  }

  function updateDayImageDescription(imageId, description) {
    setDayForm((p) => ({
      ...p,
      solvedImages: (p.solvedImages || []).map((img) =>
        img.id === imageId ? { ...img, description } : img
      ),
    }));
  }

  function handleLectureBlocksChange(nextBlocks, meta) {
    setDayForm((p) => ({
      ...p,
      contentBlocks: persistableLectureBlocks(nextBlocks),
      source: meta?.source || p.source || 'editor',
      ...(meta?.title && !String(p.title || '').trim() ? { title: meta.title } : {}),
      ...(Array.isArray(meta?.youtubeVideos) && meta.youtubeVideos.length
        ? { youtubeVideos: meta.youtubeVideos }
        : {}),
    }));
  }

  function addYoutubeVideo() {
    setDayForm((p) => ({
      ...p,
      youtubeVideos: [...(p.youtubeVideos || []), newYoutubeVideo()],
    }));
  }

  function updateYoutubeVideo(id, patch) {
    setDayForm((p) => ({
      ...p,
      youtubeVideos: (p.youtubeVideos || []).map((v) => (v.id === id ? { ...v, ...patch } : v)),
    }));
  }

  function removeYoutubeVideo(id) {
    setDayForm((p) => ({
      ...p,
      youtubeVideos: (p.youtubeVideos || []).filter((v) => v.id !== id),
    }));
  }

  async function saveDay() {
    if (!dayForm.title.trim() || !selectedModuleId) return;
    setSaving(true);
    try {
      const solvedImages = (dayForm.solvedImages || [])
        .filter((img) => img?.url)
        .map((img) => ({
          id: img.id,
          url: img.url,
          path: img.path || '',
          description: (img.description || '').trim(),
        }));
      const first = solvedImages[0];
      const isRevisionLecture = Boolean(dayForm.isRevisionLecture);
      const contentBlocks = persistableLectureBlocks(dayForm.contentBlocks);
      const authored = blocksHaveStudentContent(contentBlocks);
      const content = authored ? serializeLectureBlocks(contentBlocks) : '';
      const payload = {
        title: dayForm.title.trim(),
        topic: dayForm.topic,
        solvedImages,
        isRevisionLecture,
        contentBlocks: authored ? contentBlocks : [],
        content,
        source: authored ? (dayForm.source === 'json' ? 'json' : 'editor') : 'ai-topic',
        youtubeVideos: persistableYoutubeVideos(dayForm.youtubeVideos),
        quizQuestions: persistableQuizQuestions(dayForm.quizQuestions),
        // Keep legacy fields in sync for older clients
        solvedImageUrl: first?.url || '',
        solvedImageDescription: first?.description || '',
      };
      const currentDays = daysByModule[selectedModuleId] || [];
      let savedId = editingDayId;
      if (editingDayId) {
        await updateDoc(
          doc(db, 'modules', selectedModuleId, 'days', editingDayId),
          payload
        );
      } else {
        const ref = await addDoc(collection(db, 'modules', selectedModuleId, 'days'), {
          ...payload,
          order: currentDays.length + 1,
          createdAt: serverTimestamp(),
        });
        savedId = ref.id;
      }

      // Only one revision lecture per module
      if (isRevisionLecture && savedId) {
        const others = currentDays.filter((d) => d.id !== savedId && d.isRevisionLecture);
        if (others.length) {
          const batch = writeBatch(db);
          others.forEach((d) => {
            batch.update(doc(db, 'modules', selectedModuleId, 'days', d.id), {
              isRevisionLecture: false,
            });
          });
          await batch.commit();
        }
      }
      setShowDayModal(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminShell
      navItems={adminNav}
      title={pageMeta.title}
      subtitle={pageMeta.subtitle}
      headerExtra={<AdminNotifications />}
    >
    <div className="sba-admin-workspace" style={s.page}>
      {/* Course content subject switcher (dropdown) — only on lecture pages */}
      {isCourseTab && activeSubjectData ? (
        <div className="sba-admin-content-toolbar">
          <label className="sba-admin-subject-select-wrap">
            <span className="sba-admin-subject-select-label">Subject</span>
            <select
              className="sba-admin-subject-select"
              value={activeSubject}
              onChange={(e) => selectTab(e.target.value)}
              style={{ borderColor: activeSubjectData.color }}
            >
              {SUBJECTS.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.label} ({modules.filter((m) => m.subject === subject.id).length} modules)
                </option>
              ))}
            </select>
          </label>
          <p className="sba-admin-content-toolbar-hint">
            Use the sidebar <strong>Course Content</strong> dropdown to switch subjects anytime.
          </p>
        </div>
      ) : null}

      {/* ── Body ── */}
      <div style={s.body}>

        {activeSubject === 'dashboard' && (
          <AdminDashboardPanel onNavigate={selectTab} />
        )}

        {activeSubject === 'exam-docs' && <QuestionBankPanel />}

        {activeSubject === 'inquiries' && <InquiriesPanel />}
        {activeSubject === 'unpaid-enrollments' && <UnpaidEnrollmentsPanel />}
        {activeSubject === 'subscriptions' && <SubscriptionsPanel />}
        {activeSubject === 'pricing' && <PricingPlansPanel />}
        {activeSubject === 'payments' && <AdminPaymentsPanel />}
        {activeSubject === 'student-progress' && <AdminStudentProgressPanel />}
        {activeSubject === 'messages' && (
          <AdminMessagesPanel initialStudentId={searchParams.get('student') || ''} />
        )}

        {/* Left: Modules + Right: Lectures */}
        {isCourseTab && activeSubjectData ? (
        <>
        <div style={s.leftPanel}>
          <div style={s.panelHead}>
            <div>
              <h2 style={s.panelTitle}>
                Modules
                <span
                  style={{
                    ...s.countBadge,
                    background: activeSubjectData.color + '18',
                    color: activeSubjectData.color,
                  }}
                >
                  {filteredModules.length}
                </span>
              </h2>
              <p style={s.panelSub}>{activeSubjectData.label} — sequential order</p>
            </div>
            <button
              onClick={openAddModule}
              style={{ ...s.addBtn, background: activeSubjectData.color }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
              Add Module
            </button>
          </div>

          <div style={s.moduleListItems}>
            {filteredModules.length === 0 ? (
              <div style={s.emptyState}>
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 36, color: '#cbd5e1', marginBottom: 10 }}
                >
                  folder_open
                </span>
                <p style={s.emptyText}>No modules yet</p>
                <p style={s.emptyHint}>
                  Add modules for {activeSubjectData.label}. Students unlock them in order.
                </p>
              </div>
            ) : (
              filteredModules.map((mod, idx) => {
                const isSelected = selectedModuleId === mod.id;
                const days = daysByModule[mod.id] || [];
                const isFirst = idx === 0;
                return (
                  <div key={mod.id}>
                    {/* Chain connector */}
                    {!isFirst && (
                      <div style={s.chainConnector(activeSubjectData.color)}>
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: 16, color: activeSubjectData.color }}
                        >
                          arrow_downward
                        </span>
                        <span style={s.chainLabel}>Prerequisite</span>
                      </div>
                    )}
                    <div
                      onClick={() => setSelectedModuleId(mod.id)}
                      style={s.moduleItem(isSelected, activeSubjectData.color)}
                    >
                      <div style={s.moduleItemTop}>
                        <div
                          style={s.moduleNumBadge(isSelected, activeSubjectData.color)}
                        >
                          {idx + 1}
                        </div>
                        <div style={s.moduleItemInfo}>
                          <span style={s.moduleItemTitle}>{mod.title}</span>
                          <span style={s.moduleItemMeta}>
                            {days.length} lecture{days.length !== 1 ? 's' : ''}
                            {days.length > 1 ? ' (sequential)' : ''}
                          </span>
                        </div>
                        <div style={s.moduleItemActions}>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditModule(mod); }}
                            style={s.iconBtn('#6332ed')}
                            title="Edit"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>edit</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm({ type: 'module', id: mod.id, name: mod.title });
                            }}
                            style={s.iconBtn('#ef4444')}
                            title="Delete"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {activeSubject === 'language-arts' ? (
            <div style={{ borderTop: '1px solid #e2e8f0', padding: '14px 16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <div>
                  <h3 style={{ ...s.panelTitle, fontSize: 15, margin: 0 }}>
                    Extended Response
                    <span
                      style={{
                        ...s.countBadge,
                        background: activeSubjectData.color + '18',
                        color: activeSubjectData.color,
                      }}
                    >
                      {extendedResponses.length}
                    </span>
                  </h3>
                  <p style={{ ...s.panelSub, marginTop: 4 }}>After all Language Arts modules</p>
                </div>
                <button
                  type="button"
                  onClick={openAddEr}
                  style={{ ...s.addBtn, background: activeSubjectData.color, padding: '8px 10px', fontSize: 12 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>add</span>
                  Add
                </button>
              </div>
              {extendedResponses.length === 0 ? (
                <p style={{ ...s.emptyHint, margin: 0 }}>
                  Add a named prompt and a model answer. Students practice these after they finish every Language Arts lecture.
                </p>
              ) : (
                extendedResponses.map((item, idx) => (
                  <div
                    key={item.id}
                    style={{
                      ...s.moduleItem(false, activeSubjectData.color),
                      cursor: 'default',
                      marginBottom: 8,
                    }}
                  >
                    <div style={s.moduleItemTop}>
                      <div style={s.moduleNumBadge(false, activeSubjectData.color)}>{idx + 1}</div>
                      <div style={s.moduleItemInfo}>
                        <span style={s.moduleItemTitle}>{item.title}</span>
                        <span style={s.moduleItemMeta}>
                          {(item.prompt || '').slice(0, 80)}
                          {(item.prompt || '').length > 80 ? '…' : ''}
                        </span>
                      </div>
                      <div style={s.moduleItemActions}>
                        <button
                          type="button"
                          onClick={() => openEditEr(item)}
                          style={s.iconBtn('#6332ed')}
                          title="Edit"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>edit</span>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setDeleteConfirm({ type: 'extended', id: item.id, name: item.title })
                          }
                          style={s.iconBtn('#ef4444')}
                          title="Delete"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : null}

          {activeSubject === 'social-studies' ? (
            <div style={{ borderTop: '1px solid #e2e8f0', padding: '14px 16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <div>
                  <h3 style={{ ...s.panelTitle, fontSize: 15, margin: 0 }}>
                    Images and map Readings
                    <span
                      style={{
                        ...s.countBadge,
                        background: activeSubjectData.color + '18',
                        color: activeSubjectData.color,
                      }}
                    >
                      {imageMapReadings.length}
                    </span>
                  </h3>
                  <p style={{ ...s.panelSub, marginTop: 4 }}>Visual image · after all Social Studies modules</p>
                </div>
                <button
                  type="button"
                  onClick={openAddImr}
                  style={{ ...s.addBtn, background: activeSubjectData.color, padding: '8px 10px', fontSize: 12 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>add</span>
                  Add
                </button>
              </div>
              {imageMapReadings.length === 0 ? (
                <p style={{ ...s.emptyHint, margin: 0 }}>
                  Add a map or visual image and MCQs for it. Students study the picture and answer those questions — no AI is used.
                </p>
              ) : (
                imageMapReadings.map((item, idx) => (
                  <div
                    key={item.id}
                    style={{
                      ...s.moduleItem(false, activeSubjectData.color),
                      cursor: 'default',
                      marginBottom: 8,
                    }}
                  >
                    <div style={s.moduleItemTop}>
                      <div style={s.moduleNumBadge(false, activeSubjectData.color)}>{idx + 1}</div>
                      <div style={s.moduleItemInfo}>
                        <span style={s.moduleItemTitle}>{item.title}</span>
                        <span style={s.moduleItemMeta}>
                          {(item.images || []).length} image{(item.images || []).length === 1 ? '' : 's'}
                          {' · '}
                          {(item.questions || []).length} MCQ{(item.questions || []).length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div style={s.moduleItemActions}>
                        <button
                          type="button"
                          onClick={() => openEditImr(item)}
                          style={s.iconBtn('#6332ed')}
                          title="Edit"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>edit</span>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setDeleteConfirm({ type: 'image-map', id: item.id, name: item.title })
                          }
                          style={s.iconBtn('#ef4444')}
                          title="Delete"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
        <div style={s.rightPanel}>
          {!selectedModule ? (
            <div style={s.emptyState}>
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 44, color: '#cbd5e1', marginBottom: 12 }}
              >
                touch_app
              </span>
              <p style={s.emptyText}>Select a module</p>
              <p style={s.emptyHint}>
                Click a module on the left to manage its lectures.
              </p>
            </div>
          ) : (
            <>
              <div style={s.panelHead}>
                <div>
                  <h2 style={s.panelTitle}>
                    Lectures
                    <span
                      style={{
                        ...s.countBadge,
                        background: activeSubjectData.color + '18',
                        color: activeSubjectData.color,
                      }}
                    >
                      {selectedDays.length}
                    </span>
                  </h2>
                  <p style={s.panelSub}>
                    {selectedModule.title} — each lecture unlocks the next
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    onClick={openAddDay}
                    style={{ ...s.addBtn, background: activeSubjectData.color }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
                    Add Lecture
                  </button>
                  <button
                    type="button"
                    onClick={openAddRevisionDay}
                    style={{
                      ...s.addBtn,
                      background: '#fff',
                      color: '#b45309',
                      border: '1.5px solid #f59e0b66',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>replay</span>
                    Add Revision Lecture
                  </button>
                </div>
              </div>

              <div style={s.dayListItems}>
                {selectedDays.length === 0 ? (
                  <div style={s.emptyState}>
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 36, color: '#cbd5e1', marginBottom: 10 }}
                    >
                      article
                    </span>
                    <p style={s.emptyText}>No lectures yet</p>
                    <p style={s.emptyHint}>
                      Add regular lectures, then use <strong>Add Revision Lecture</strong> (or check “Module Revision” when editing) so students can revise the module.
                    </p>
                  </div>
                ) : (
                  selectedDays.map((day, idx) => (
                    <div key={day.id} style={s.dayItem(activeSubjectData.color)}>
                      <div style={s.dayItemLeft}>
                        <div style={s.daySequence(activeSubjectData.color)}>
                          <span style={s.daySeqNum(activeSubjectData.color)}>
                            {idx + 1}
                          </span>
                          {idx < selectedDays.length - 1 && (
                            <div style={s.daySeqLine(activeSubjectData.color)} />
                          )}
                        </div>
                        <div style={s.dayItemContent}>
                          <p style={s.dayItemTitle}>{day.title}</p>
                          <p style={s.dayItemPreview}>
                            {hasAuthoredLecture(day)
                              ? (day.content || '').replace(/\[\[.*?\]\]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 130) +
                                ((day.content || '').length > 130 ? '…' : '')
                              : day.topic
                                ? day.topic.slice(0, 130) + (day.topic.length > 130 ? '…' : '')
                                : 'No lecture yet — click Edit to write or import JSON.'}
                          </p>
                          {hasAuthoredLecture(day) ? (
                            <span style={s.aiTagSmall}>
                              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>edit_note</span>
                              {day.source === 'json' ? 'Imported JSON' : 'Admin lecture'}
                            </span>
                          ) : day.topic ? (
                            <span style={s.aiTagSmall}>
                              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>auto_awesome</span>
                              AI Generated
                            </span>
                          ) : null}
                          {day.isRevisionLecture && (
                            <span style={{ ...s.aiTagSmall, marginLeft: 6, background: '#fffbeb', color: '#b45309' }}>
                              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>replay</span>
                              Module Revision
                            </span>
                          )}
                          {idx > 0 && (
                            <p style={s.prereqNote}>
                              🔒 Unlocks after completing Lecture {idx}
                            </p>
                          )}
                        </div>
                      </div>
                      <div style={s.dayItemActions}>
                        <button
                          onClick={() => openEditDay(day)}
                          style={s.actionBtn(activeSubjectData.color, activeSubjectData.color + '18')}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>edit</span>
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            setDeleteConfirm({ type: 'day', id: day.id, name: day.title })
                          }
                          style={s.actionBtn('#ef4444', '#fef2f2')}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
        </>
        ) : null}

      </div>

      {/* ── Module Modal ── */}
      {showModuleModal && (
        <Modal
          title={editingModuleId ? 'Edit Module' : `Add Module — ${activeSubjectData.label}`}
          onClose={() => setShowModuleModal(false)}
          onSave={saveModule}
          saving={saving}
          disabled={!moduleForm.title.trim()}
          extraWide
          accentColor={activeSubjectData.color}
        >
          <label style={s.formLabel}>Module Title</label>
          <input
            style={s.input}
            value={moduleForm.title}
            onChange={(e) => setModuleForm((p) => ({ ...p, title: e.target.value }))}
            placeholder={`e.g. Module ${filteredModules.length + 1}: Introduction to ${activeSubjectData.label}`}
            autoFocus
          />
          {!editingModuleId && (
            <p style={s.formHint}>
              This will be Module {filteredModules.length + 1} of {activeSubjectData.label}.
              {filteredModules.length > 0
                ? ` Students must complete Module ${filteredModules.length} before unlocking this one.`
                : ' This will be the first module — always unlocked.'}
            </p>
          )}
          <div style={{ marginTop: 20 }}>
            <QuizMcqEditor
              questions={moduleForm.quizQuestions || []}
              onChange={(quizQuestions) => setModuleForm((p) => ({ ...p, quizQuestions }))}
              allowMedia
              moduleId={editingModuleId || selectedModuleId || 'draft'}
              label="Module exam questions"
              hint="Extra module-exam items (optional). Upload a DOCX in the same format as the practice exams, including pictures, or add questions here. Students get 25 shuffled questions in the exam window. Save the module after uploading."
            />
          </div>
        </Modal>
      )}

      {showErModal && (
        <Modal
          title={editingErId ? 'Edit Extended Response' : 'Add Extended Response — Language Arts'}
          onClose={() => setShowErModal(false)}
          onSave={saveExtendedResponse}
          saving={saving}
          disabled={!erForm.title.trim() || !erForm.prompt.trim() || !erForm.modelAnswer.trim()}
          wide
          accentColor={activeSubjectData?.color}
        >
          <label style={s.formLabel}>Name</label>
          <input
            style={s.input}
            value={erForm.title}
            onChange={(e) => setErForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="e.g. Community garden debate"
            autoFocus
          />
          <label style={{ ...s.formLabel, marginTop: 16 }}>Extended response (prompt / passages)</label>
          <textarea
            style={s.textarea}
            rows={8}
            value={erForm.prompt}
            onChange={(e) => setErForm((p) => ({ ...p, prompt: e.target.value }))}
            placeholder="Paste the GED-style prompt and source passages students will read."
          />
          <label style={{ ...s.formLabel, marginTop: 16 }}>Answer (model extended response)</label>
          <textarea
            style={s.textarea}
            rows={8}
            value={erForm.modelAnswer}
            onChange={(e) => setErForm((p) => ({ ...p, modelAnswer: e.target.value }))}
            placeholder="Write a strong exemplar. Students will not see this; it guides AI scoring."
          />
          <p style={s.formHint}>
            Students practice this after all Language Arts modules. Scoring uses the official GED 3-trait rubric (argument/evidence, organization, Standard English). The model answer is an exemplar, not a copy-match key.
          </p>
        </Modal>
      )}

      {showImrModal && (
        <Modal
          title={editingImrId ? 'Edit Images and map Readings' : 'Add Images and map Readings — Social Studies'}
          onClose={() => setShowImrModal(false)}
          onSave={saveImageMapReading}
          saving={saving}
          disabled={!canSaveImr}
          wide
          accentColor={activeSubjectData?.color}
        >
          <label style={s.formLabel}>Name</label>
          <input
            style={s.input}
            value={imrForm.title}
            onChange={(e) => setImrForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="e.g. U.S. electoral map, 2020"
            autoFocus
          />

          <div style={{ marginTop: 18 }}>
            <div style={s.aiLabelRow}>
              <label style={s.formLabel}>Visual image</label>
              <div style={s.aiBadge}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>map</span>
                Map or picture — no AI
              </div>
            </div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
              border: '1.5px dashed #cbd5e1', borderRadius: 12, cursor: 'pointer', marginBottom: 12,
              background: '#f8fafc',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22, color: activeSubjectData?.color }}>
                upload_file
              </span>
              <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                {imrImageUploading ? 'Uploading…' : 'Upload map or visual image (PNG, JPG)'}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImrImageUpload}
                disabled={imrImageUploading}
                style={{ display: 'none' }}
              />
            </label>
            {(imrForm.images || []).map((img, index) => (
              <div
                key={img.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 14,
                  marginBottom: 12,
                  background: '#fff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <strong style={{ fontSize: 13, color: '#1b1b1d' }}>Visual image {index + 1}</strong>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{
                      fontSize: 12, fontWeight: 700, color: activeSubjectData?.color,
                      cursor: replacingImrImageId === img.id ? 'wait' : 'pointer',
                      padding: '6px 10px', borderRadius: 8, background: '#f1f5f9',
                    }}>
                      {replacingImrImageId === img.id ? 'Replacing…' : 'Replace'}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={!!replacingImrImageId}
                        onChange={(e) => handleReplaceImrImage(e, img.id)}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => handleDeleteImrImage(img.id)}
                      style={{
                        fontSize: 12, fontWeight: 700, color: '#dc2626', border: 'none',
                        background: '#fef2f2', padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <img
                  src={img.url}
                  alt={`Visual image ${index + 1}`}
                  style={{
                    width: '100%', maxHeight: 180, objectFit: 'contain',
                    borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 10,
                  }}
                />
                <label style={s.formLabel}>Caption (optional, shown to students)</label>
                <textarea
                  style={s.textarea}
                  value={img.description || ''}
                  onChange={(e) =>
                    setImrForm((p) => ({
                      ...p,
                      images: (p.images || []).map((row) =>
                        row.id === img.id ? { ...row, description: e.target.value } : row
                      ),
                    }))
                  }
                  placeholder="e.g. Electoral votes by state. Blue = Democrat, Red = Republican."
                  rows={2}
                />
              </div>
            ))}
          </div>

          <label style={{ ...s.formLabel, marginTop: 8 }}>Reading (optional)</label>
          <textarea
            style={s.textarea}
            rows={4}
            value={imrForm.readingText}
            onChange={(e) => setImrForm((p) => ({ ...p, readingText: e.target.value }))}
            placeholder="Short passage or map-reading notes students should study with the image."
          />

          <div style={{ marginTop: 18 }}>
            <div style={s.aiLabelRow}>
              <label style={s.formLabel}>MCQs for this image</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    const blob = new Blob([sampleImageMapJsonText()], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'image-map-reading-sample.json';
                    link.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{ ...s.addBtn, background: '#f1f5f9', color: '#334155' }}
                >
                  Sample JSON
                </button>
                <label style={{ ...s.addBtn, background: '#f1f5f9', color: '#334155', cursor: 'pointer' }}>
                  Upload JSON
                  <input
                    type="file"
                    accept="application/json,.json"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        try {
                          const questions = importImageMapJson(String(reader.result || ''));
                          setImrJsonError('');
                          setImrForm((prev) => ({ ...prev, questions }));
                        } catch (err) {
                          setImrJsonError(err.message || 'Could not read that JSON file.');
                        }
                      };
                      reader.onerror = () => setImrJsonError('Could not read that JSON file.');
                      reader.readAsText(file);
                    }}
                  />
                </label>
              </div>
            </div>
            {imrJsonError ? <p style={{ ...s.formHint, color: '#dc2626' }}>{imrJsonError}</p> : null}
            {(imrForm.questions || []).map((q, qIdx) => (
              <div
                key={qIdx}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 14,
                  marginBottom: 12,
                  background: '#f8fafc',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong style={{ fontSize: 13 }}>Question {qIdx + 1}</strong>
                  {(imrForm.questions || []).length > 1 ? (
                    <button
                      type="button"
                      onClick={() =>
                        setImrForm((p) => ({
                          ...p,
                          questions: p.questions.filter((_, i) => i !== qIdx),
                        }))
                      }
                      style={{
                        fontSize: 12, fontWeight: 700, color: '#dc2626', border: 'none',
                        background: 'transparent', cursor: 'pointer',
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
                <textarea
                  style={s.textarea}
                  rows={2}
                  value={q.question}
                  onChange={(e) => updateImrQuestion(qIdx, { question: e.target.value })}
                  placeholder="Ask something students can only answer by reading the image or map."
                />
                {[0, 1, 2, 3].map((optIdx) => (
                  <label
                    key={optIdx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    <input
                      type="radio"
                      name={`imr-correct-${qIdx}`}
                      checked={Number(q.correct) === optIdx}
                      onChange={() => updateImrQuestion(qIdx, { correct: optIdx })}
                    />
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', width: 16 }}>
                      {String.fromCharCode(65 + optIdx)}
                    </span>
                    <input
                      style={{ ...s.input, margin: 0 }}
                      value={q.options?.[optIdx] || ''}
                      onChange={(e) => updateImrOption(qIdx, optIdx, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                    />
                  </label>
                ))}
                <label style={{ ...s.formLabel, marginTop: 10 }}>Explanation (optional, shown after submit)</label>
                <textarea
                  style={s.textarea}
                  rows={2}
                  value={q.explanation || ''}
                  onChange={(e) => updateImrQuestion(qIdx, { explanation: e.target.value })}
                  placeholder="Why the marked option is correct, based on the image."
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setImrForm((p) => ({
                  ...p,
                  questions: [...(p.questions || []), blankImageMapMcq()],
                }))
              }
              style={{
                ...s.addBtn,
                background: '#f1f5f9',
                color: '#334155',
                marginTop: 4,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
              Add MCQ
            </button>
          </div>
          <p style={s.formHint}>
            Students see this after all Social Studies lectures. Mark the correct option with the radio button. Questions are saved as-is — the quiz does not call an API.
          </p>
        </Modal>
      )}

      {/* ── Day Modal ── */}
      {showDayModal && (
        <Modal
          title={
            editingDayId
              ? dayForm.isRevisionLecture
                ? 'Edit Revision Lecture'
                : 'Edit Lecture'
              : dayForm.isRevisionLecture
                ? 'Add Revision Lecture'
                : 'Add Lecture'
          }
          onClose={() => setShowDayModal(false)}
          onSave={saveDay}
          saving={saving}
          disabled={!dayForm.title.trim()}
          wide
          extraWide
          accentColor={activeSubjectData.color}
        >
          <label style={s.formLabel}>Lecture Title</label>
          <input
            style={s.input}
            value={dayForm.title}
            onChange={(e) => setDayForm((p) => ({ ...p, title: e.target.value }))}
            placeholder={`e.g. Lecture ${selectedDays.length + 1}: Topic Name`}
            autoFocus
          />
          {!editingDayId && selectedDays.length > 0 && (
            <p style={s.formHint}>
              This will be Lecture {selectedDays.length + 1}. Students must complete Lecture {selectedDays.length} before accessing it.
            </p>
          )}

          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              marginTop: 16,
              padding: '12px 14px',
              borderRadius: 12,
              border: dayForm.isRevisionLecture ? '1.5px solid #f59e0b66' : '1.5px solid #e2e8f0',
              background: dayForm.isRevisionLecture ? '#fffbeb' : '#f8fafc',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={Boolean(dayForm.isRevisionLecture)}
              onChange={(e) =>
                setDayForm((p) => ({ ...p, isRevisionLecture: e.target.checked }))
              }
              style={{ marginTop: 3, width: 16, height: 16 }}
            />
            <span>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                This is a Module Revision lecture
              </span>
              <span style={{ display: 'block', fontSize: 12, color: '#78716c', marginTop: 4, lineHeight: 1.45 }}>
                Only one revision lecture is kept active per module. Write the revision in the editor below (or import JSON).
              </span>
            </span>
          </label>

          <div style={{ marginTop: 18 }}>
            <LectureEditor
              blocks={dayForm.contentBlocks || []}
              onChange={handleLectureBlocksChange}
              moduleId={selectedModuleId}
              dayId={editingDayId}
              accentColor={activeSubjectData.color}
            />
          </div>

          <div style={{ marginTop: 22 }}>
            <QuizMcqEditor
              questions={dayForm.quizQuestions || []}
              onChange={(quizQuestions) => setDayForm((p) => ({ ...p, quizQuestions }))}
              label="Lecture quiz"
              hint="Add as many MCQs as you want in the editor or JSON. Students see 5 shuffled questions each try. Retry pulls 5 different questions from the rest of the pool when you have more than 5."
            />
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={s.formLabel}>Internal notes (optional)</label>
            <textarea
              style={s.textarea}
              value={dayForm.topic}
              onChange={(e) => setDayForm((p) => ({ ...p, topic: e.target.value }))}
              placeholder="Staff-only notes. If the lecture editor is empty, students can still get an AI lesson from these notes (legacy)."
              rows={3}
            />
          </div>

          <div style={{ marginTop: 22 }}>
            <label style={s.formLabel}>YouTube videos (optional)</label>
            <p style={s.formHint}>
              Paste watch or share links. Students see these embeds — no YouTube search or AI lookup.
            </p>
            {(dayForm.youtubeVideos || []).map((video, index) => (
              <div
                key={video.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 12,
                  marginBottom: 10,
                  background: '#fff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <strong style={{ fontSize: 13 }}>Video {index + 1}</strong>
                  <button
                    type="button"
                    onClick={() => removeYoutubeVideo(video.id)}
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#dc2626',
                      border: 'none',
                      background: '#fef2f2',
                      padding: '6px 10px',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  >
                    Remove
                  </button>
                </div>
                <input
                  style={{ ...s.input, marginBottom: 8 }}
                  value={video.title || ''}
                  onChange={(e) => updateYoutubeVideo(video.id, { title: e.target.value })}
                  placeholder="Title (optional)"
                />
                <input
                  style={s.input}
                  value={video.url || ''}
                  onChange={(e) => updateYoutubeVideo(video.id, { url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
            ))}
            <button
              type="button"
              onClick={addYoutubeVideo}
              style={{ ...s.addBtn, background: '#f1f5f9', color: '#334155' }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>smart_display</span>
              Add YouTube link
            </button>
          </div>

          <div style={{ marginTop: 22 }}>
            <div style={s.aiLabelRow}>
              <label style={s.formLabel}>Pictorial worked solutions (optional)</label>
              <div style={s.aiBadge}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>image</span>
                AI teaches from these pictures
              </div>
            </div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
              border: '1.5px dashed #cbd5e1', borderRadius: 12, cursor: 'pointer', marginBottom: 12,
              background: '#f8fafc',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22, color: activeSubjectData.color }}>
                upload_file
              </span>
              <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                {dayImageUploading
                  ? 'Uploading…'
                  : 'Upload one or more images (PNG, JPG)'}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleDayImageUpload}
                disabled={dayImageUploading}
                style={{ display: 'none' }}
              />
            </label>

            {(dayForm.solvedImages || []).map((img, index) => (
              <div
                key={img.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 14,
                  marginBottom: 12,
                  background: '#fff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <strong style={{ fontSize: 13, color: '#1b1b1d' }}>Image {index + 1}</strong>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{
                      fontSize: 12, fontWeight: 700, color: activeSubjectData.color,
                      cursor: replacingImageId === img.id ? 'wait' : 'pointer',
                      padding: '6px 10px', borderRadius: 8, background: '#f1f5f9',
                    }}>
                      {replacingImageId === img.id ? 'Replacing…' : 'Replace'}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={!!replacingImageId}
                        onChange={(e) => handleReplaceDayImage(e, img.id)}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => handleDeleteDayImage(img.id)}
                      style={{
                        fontSize: 12, fontWeight: 700, color: '#dc2626', border: 'none',
                        background: '#fef2f2', padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <img
                  src={img.url}
                  alt={`Worked solution ${index + 1}`}
                  style={{
                    width: '100%', maxHeight: 140, objectFit: 'contain',
                    borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 10,
                  }}
                />
                <label style={s.formLabel}>Description for this image (for AI teaching)</label>
                <textarea
                  style={s.textarea}
                  value={img.description || ''}
                  onChange={(e) => updateDayImageDescription(img.id, e.target.value)}
                  placeholder={`Describe every step shown in image ${index + 1}.\n\nExample: "Step 1 subtract 5 from both sides… Step 2 divide by 2…"`}
                  rows={4}
                />
              </div>
            ))}

            <p style={s.formHint}>
              You can add multiple pictures, each with its own description. Replace or delete any image anytime — deletes remove the file from the server.
            </p>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirm ── */}
      {deleteConfirm && (
        <Modal
          title="Confirm Delete"
          onClose={() => setDeleteConfirm(null)}
          onSave={executeDelete}
          saving={saving}
          saveLabel="Delete"
          danger
        >
          <p style={s.deleteText}>
            Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>?
            {deleteConfirm.type === 'module' &&
              ' All lectures inside this module will also be deleted.'}
            {deleteConfirm.type === 'extended' &&
              ' Students will no longer see this Language Arts extended response.'}
            {deleteConfirm.type === 'image-map' &&
              ' Students will no longer see this Social Studies image and map reading.'}
          </p>
          <p style={s.deleteHint}>This action cannot be undone.</p>
        </Modal>
      )}
    </div>
    </AdminShell>
  );
}

/* ── Modal component — scrollable body, sticky footer so Save never overflows ── */
function Modal({ title, children, onClose, onSave, saving, disabled, saveLabel, danger, wide, extraWide, accentColor }) {
  return (
    <div style={ms.overlay} onClick={onClose}>
      <div
        style={{ ...ms.modal, maxWidth: extraWide ? 880 : wide ? 720 : 480 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={{ ...ms.modalHead, borderTop: `3px solid ${accentColor || (danger ? '#ef4444' : '#1b365d')}` }}>
          <h3 style={ms.modalTitle}>{title}</h3>
          <button type="button" onClick={onClose} style={ms.closeBtn} aria-label="Close">
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
          </button>
        </div>
        <div style={ms.modalBody}>{children}</div>
        <div style={ms.modalFoot}>
          <button type="button" onClick={onClose} style={ms.cancelBtn}>Cancel</button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || disabled}
            style={{
              ...ms.saveBtn,
              background: danger ? '#ef4444' : (accentColor || '#1b365d'),
              opacity: saving || disabled ? 0.6 : 1,
              cursor: saving || disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : (saveLabel || 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Styles ── */
const s = {
  page: {
    minHeight: 0,
    background: 'transparent',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  header: {
    background: '#1b365d',
    padding: '18px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  headerIcon: { fontSize: 28, color: '#c5a046' },
  headerTitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 19,
    fontWeight: 800,
    color: '#fff',
    letterSpacing: '-0.02em',
  },
  headerSub: { fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  headerBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8, color: 'rgba(255,255,255,0.75)',
    fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '8px 14px',
  },
  headerBtnDanger: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 8, color: '#fca5a5',
    fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '8px 14px',
  },

  /* Subject tabs */
  subjectBar: {
    display: 'flex',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    padding: '0 24px',
    gap: 0,
    flexShrink: 0,
  },
  subjectTab: (active, color) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '14px 20px',
    borderBottom: active ? `2.5px solid ${color}` : '2.5px solid transparent',
    transition: 'border-color 0.15s',
    fontSize: 14, fontWeight: active ? 700 : 500,
  }),
  subjectTabLabel: (active) => ({
    color: active ? '#1b1b1d' : '#64748b',
  }),
  subjectTabBadge: (active, color) => ({
    background: active ? color + '18' : '#f1f5f9',
    color: active ? color : '#94a3b8',
    borderRadius: 12, padding: '2px 8px',
    fontSize: 11, fontWeight: 700,
  }),

  /* Body */
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  leftPanel: {
    width: 340, flexShrink: 0,
    background: '#fff',
    borderRight: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column', overflowY: 'auto',
  },
  rightPanel: {
    flex: 1, overflowY: 'auto',
    display: 'flex', flexDirection: 'column',
  },
  panelHead: {
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '18px 20px 14px',
    borderBottom: '1px solid #f1f5f9',
    flexShrink: 0, background: '#fff',
    position: 'sticky', top: 0, zIndex: 1,
  },
  panelTitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 15, fontWeight: 800, color: '#1b1b1d',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  panelSub: { fontSize: 11, color: '#94a3b8', marginTop: 3, fontWeight: 500 },
  countBadge: {
    borderRadius: 20, padding: '2px 8px', fontSize: 12, fontWeight: 700,
  },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 14px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
  },

  /* Module list */
  moduleListItems: { flex: 1, overflowY: 'auto', padding: '12px 0' },
  chainConnector: (color) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '4px 20px',
    color,
  }),
  chainLabel: { fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase' },
  moduleItem: (active, color) => ({
    margin: '0 12px', borderRadius: 12,
    border: active ? `1.5px solid ${color}` : '1.5px solid #e2e8f0',
    background: active ? color + '08' : '#fff',
    cursor: 'pointer', padding: '14px',
    transition: 'border-color 0.15s, background 0.15s',
  }),
  moduleItemTop: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  moduleNumBadge: (active, color) => ({
    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
    background: active ? color : '#f1f5f9',
    color: active ? '#fff' : '#94a3b8',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 13, fontWeight: 800,
  }),
  moduleItemInfo: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  moduleItemTitle: { fontSize: 13, fontWeight: 600, color: '#1b1b1d', lineHeight: 1.4 },
  moduleItemMeta: { fontSize: 11, color: '#94a3b8', fontWeight: 500 },
  moduleItemActions: { display: 'flex', gap: 4, flexShrink: 0 },
  iconBtn: (color) => ({
    background: 'none', border: 'none', cursor: 'pointer', color,
    display: 'flex', alignItems: 'center', padding: 5, borderRadius: 6, opacity: 0.7,
  }),

  /* Day list */
  dayListItems: { padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 0 },
  dayItem: (color) => ({
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 12, padding: '16px 0',
    borderBottom: '1px solid #f1f5f9',
  }),
  dayItemLeft: { display: 'flex', alignItems: 'flex-start', gap: 16, flex: 1, minWidth: 0 },
  daySequence: (color) => ({
    display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0,
  }),
  daySeqNum: (color) => ({
    width: 30, height: 30, borderRadius: '50%',
    background: color + '18', color,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 13, fontWeight: 800, flexShrink: 0,
  }),
  daySeqLine: (color) => ({
    width: 2, flex: 1, minHeight: 24,
    background: color + '30',
    marginTop: 4,
  }),
  dayItemContent: { flex: 1, minWidth: 0, paddingBottom: 8 },
  dayItemTitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 14, fontWeight: 700, color: '#1b1b1d', marginBottom: 6,
  },
  dayItemPreview: { fontSize: 12, color: '#94a3b8', lineHeight: 1.5, marginBottom: 6 },
  prereqNote: { fontSize: 11, color: '#f59e0b', fontWeight: 500 },
  dayItemActions: { display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 },
  actionBtn: (color, bg) => ({
    display: 'flex', alignItems: 'center', gap: 5,
    background: bg, color, border: 'none', borderRadius: 8,
    padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  }),

  /* Misc */
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '60px 24px', textAlign: 'center', flex: 1,
  },
  emptyText: { fontSize: 14, fontWeight: 600, color: '#94a3b8', marginBottom: 6 },
  emptyHint: { fontSize: 12, color: '#cbd5e1', lineHeight: 1.5, maxWidth: 260 },
  formLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 },
  formHint: { fontSize: 12, color: '#64748b', marginTop: 8, lineHeight: 1.5, fontStyle: 'italic' },
  input: {
    width: '100%', padding: '10px 12px', fontSize: 14,
    border: '1.5px solid #e2e8f0', borderRadius: 8, outline: 'none',
    fontFamily: 'Plus Jakarta Sans, sans-serif', color: '#1b1b1d', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '12px', fontSize: 14,
    border: '1.5px solid #e2e8f0', borderRadius: 8, outline: 'none',
    fontFamily: 'Plus Jakarta Sans, sans-serif', color: '#1b1b1d',
    resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box',
  },
  deleteText: { fontSize: 14, color: '#374151', lineHeight: 1.6, marginBottom: 8 },
  deleteHint: { fontSize: 12, color: '#ef4444', fontWeight: 500 },
  aiLabelRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8,
  },
  aiBadge: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'linear-gradient(135deg, rgba(75,0,209,0.08), #f0fdf4)',
    color: '#6332ed', border: '1px solid rgba(75,0,209,0.2)',
    borderRadius: 20, padding: '4px 10px',
    fontSize: 11, fontWeight: 600,
  },
  aiTagSmall: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: 'rgba(75,0,209,0.08)', color: '#6332ed',
    borderRadius: 20, padding: '2px 8px',
    fontSize: 10, fontWeight: 600,
  },
};

const ms = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxHeight: 'min(92vh, 900px)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 25px 60px rgba(0,0,0,0.22)',
    overflow: 'hidden',
  },
  modalHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 22px',
    borderBottom: '1px solid #f1f5f9',
    flexShrink: 0,
  },
  modalTitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16, fontWeight: 800, color: '#1b1b1d', letterSpacing: '-0.01em',
    margin: 0,
  },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#94a3b8', display: 'flex', alignItems: 'center', padding: 4, borderRadius: 6,
  },
  modalBody: {
    padding: '18px 22px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
    WebkitOverflowScrolling: 'touch',
  },
  modalFoot: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '14px 22px',
    borderTop: '1px solid #e2e8f0',
    background: '#f8fafc',
    flexShrink: 0,
    position: 'sticky',
    bottom: 0,
  },
  cancelBtn: {
    background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 8,
    color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '9px 20px',
  },
  saveBtn: {
    color: '#fff', border: 'none', borderRadius: 8,
    fontSize: 14, fontWeight: 600, padding: '9px 24px',
  },
};
